import { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Building2, Mail, Phone, Users, MapPin, Network } from 'lucide-react';
import useFetch from '../../hooks/useFetch';
import { post, patch } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import {
  Card, Button, Modal, Field, Input, Select, Badge, Loader, EmptyState,
  SearchInput, SectionTitle, stagger, fadeUp,
} from '../../components/UI';

const BLANK = {
  name: '', code: '', industry: '', parent: '',
  contactPerson: '', contactDesignation: '', contactEmail: '', contactMobile: '',
  city: '', state: '', gstNumber: '',
};

export default function Corporates() {
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [saving, setSaving] = useState(false);

  const { data: rows, loading, reload } = useFetch('/corporates', { limit: 200, ...(search ? { search } : {}) });

  const openNew = () => { setEditing(null); setForm(BLANK); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c._id);
    setForm({
      name: c.name || '', code: c.code || '', industry: c.industry || '',
      parent: c.parent?._id || c.parent || '',
      contactPerson: c.contactPerson || '', contactDesignation: c.contactDesignation || '',
      contactEmail: c.contactEmail || '', contactMobile: c.contactMobile || '',
      city: c.address?.city || '', state: c.address?.state || '', gstNumber: c.gstNumber || '',
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error('Company name is required');
    setSaving(true);
    const payload = {
      name: form.name.trim(), code: form.code || undefined, industry: form.industry,
      parent: form.parent || null,
      contactPerson: form.contactPerson, contactDesignation: form.contactDesignation,
      contactEmail: form.contactEmail, contactMobile: form.contactMobile,
      gstNumber: form.gstNumber,
      address: { city: form.city, state: form.state },
    };
    try {
      if (editing) await patch(`/corporates/${editing}`, payload);
      else await post('/corporates', payload);
      toast.success(editing ? 'Corporate updated' : 'Corporate added');
      setOpen(false);
      reload();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SectionTitle
        title="Corporate accounts"
        subtitle="Client companies whose employees attend your events"
        action={<Button icon={Plus} onClick={openNew}>Add corporate</Button>}
      />

      <SearchInput
        value={search} onChange={(e) => setSearch(e.target.value)}
        placeholder="Search companies…" style={{ maxWidth: 380, marginBottom: 18 }}
      />

      {loading ? <Loader label="Loading corporates" /> : !rows?.length ? (
        <EmptyState
          message={search ? 'No companies match your search' : 'No corporate accounts yet'}
          hint="Add a client company, then upload its employee list against an event."
          icon={Building2}
          action={!search && <Button icon={Plus} onClick={openNew}>Add corporate</Button>}
        />
      ) : (
        <motion.div variants={stagger} initial="hidden" animate="show"
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 15 }}>
          {rows.map((c) => (
            <motion.div key={c._id} variants={fadeUp}>
              <Card hover onClick={() => openEdit(c)} style={{ cursor: 'pointer', height: '100%' }}>
                <div style={{ display: 'flex', gap: 13, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 44, height: 44, borderRadius: 'var(--r)', flexShrink: 0,
                    background: c.logo ? `url(${c.logo}) center/cover` : 'rgba(239,212,138,0.1)',
                    display: 'grid', placeItems: 'center',
                  }}>
                    {!c.logo && <Building2 size={20} color="var(--gold-500)" />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <h3 style={{ fontSize: 14.5, fontWeight: 600, lineHeight: 1.35 }}>{c.name}</h3>
                    <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
                      {c.parent?.name && <Badge tone="neutral"><Network size={10} /> under {c.parent.name}</Badge>}
                      {c.childCount > 0 && <Badge tone="gold">{c.childCount} {c.childCount === 1 ? 'division' : 'divisions'}</Badge>}
                      {c.code && <Badge tone="gold">{c.code}</Badge>}
                      {c.industry && <Badge tone="neutral">{c.industry}</Badge>}
                      {!c.isActive && <Badge tone="danger">Inactive</Badge>}
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: 15, paddingTop: 14, borderTop: '1px solid var(--ink-750)', display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12.3, color: 'var(--text-mid)' }}>
                  {c.contactPerson && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Users size={13} color="var(--gold-600)" /> {c.contactPerson}{c.contactDesignation ? ` · ${c.contactDesignation}` : ''}
                    </span>
                  )}
                  {c.contactEmail && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      <Mail size={13} color="var(--gold-600)" />
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.contactEmail}</span>
                    </span>
                  )}
                  {c.contactMobile && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Phone size={13} color="var(--gold-600)" /> {c.contactMobile}
                    </span>
                  )}
                  {c.address?.city && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <MapPin size={13} color="var(--gold-600)" /> {c.address.city}{c.address.state ? `, ${c.address.state}` : ''}
                    </span>
                  )}
                </div>

                {c.employeeCount !== undefined && (
                  <div style={{ marginTop: 13, display: 'flex', alignItems: 'baseline', gap: 7 }}>
                    <span style={{ fontSize: 19, fontWeight: 700, color: 'var(--gold-400)' }}>{c.employeeCount}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-lo)' }}>employees on the platform</span>
                  </div>
                )}
              </Card>
            </motion.div>
          ))}
        </motion.div>
      )}

      <Modal
        open={open} onClose={() => setOpen(false)}
        title={editing ? 'Edit corporate' : 'Add a corporate account'}
        footer={
          <>
            <Button variant="subtle" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} loading={saving}>{editing ? 'Save changes' : 'Add corporate'}</Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
          <Field label="Company name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus placeholder="Meridian Pharmaceuticals" />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Short code" hint="Used on reports">
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="MERID" />
            </Field>
            <Field label="Industry">
              <Input value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} placeholder="Pharmaceuticals" />
            </Field>
          </div>
          <Field
            label="Part of a bigger company?"
            hint="Leave blank for a company that stands on its own. Divisions can have their own divisions under them."
          >
            <Select value={form.parent} onChange={(e) => setForm({ ...form, parent: e.target.value })}>
              <option value="">Not part of another company</option>
              {(rows || []).filter((c) => c._id !== editing).map((c) => (
                <option key={c._id} value={c._id}>{c.parent?.name ? `${c.parent.name} → ${c.name}` : c.name}</option>
              ))}
            </Select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Representative" hint="Shown wherever this company is picked">
              <Input value={form.contactPerson} onChange={(e) => setForm({ ...form, contactPerson: e.target.value })} placeholder="Rakesh Menon" />
            </Field>
            <Field label="Their role">
              <Input value={form.contactDesignation} onChange={(e) => setForm({ ...form, contactDesignation: e.target.value })} placeholder="Regional Head" />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="Contact email">
              <Input type="email" value={form.contactEmail} onChange={(e) => setForm({ ...form, contactEmail: e.target.value })} />
            </Field>
            <Field label="Contact mobile">
              <Input value={form.contactMobile} onChange={(e) => setForm({ ...form, contactMobile: e.target.value })} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 13 }}>
            <Field label="City">
              <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
            </Field>
            <Field label="State">
              <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} />
            </Field>
          </div>
          <Field label="GST number">
            <Input value={form.gstNumber} onChange={(e) => setForm({ ...form, gstNumber: e.target.value.toUpperCase() })} placeholder="06AABCM1234K1Z5" />
          </Field>
        </div>
      </Modal>
    </>
  );
}
